// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Distance presentation. Everything in the app is stored and computed in metres — course
// data, GPS traces, derived geometry — and only ever converted at the point of display, so
// switching the preference can never change what is saved.

/** The player's preferred units. Golf uses yards, not feet, for hole distances. */
export type DistanceUnit = 'metric' | 'imperial';

export const DEFAULT_DISTANCE_UNIT: DistanceUnit = 'metric';

const YARDS_PER_METRE = 1.0936133;
const FEET_PER_METRE = 3.2808399;
const METRES_PER_MILE = 1609.344;

/** Reads a stored value, falling back to metric for anything unrecognised. */
export const parseDistanceUnit = (value: unknown): DistanceUnit =>
  value === 'imperial' ? 'imperial' : DEFAULT_DISTANCE_UNIT;

export const metresToYards = (metres: number): number => metres * YARDS_PER_METRE;

export const yardsToMetres = (yards: number): number => yards / YARDS_PER_METRE;

/**
 * Formats a hole or shot distance — the everyday case, always whole units.
 *
 * Golf never quotes a hole to the decimetre, and a yard is close enough to a metre that
 * fractions would only add noise.
 */
export const formatHoleDistance = (metres: number, unit: DistanceUnit): string => {
  if (!Number.isFinite(metres)) return '';
  return unit === 'imperial'
    ? `${Math.round(metresToYards(metres))} yd`
    : `${Math.round(metres)} m`;
};

/**
 * Formats a mapped area — a bunker, a green surface, a stretch of fairway.
 *
 * Whole units, because these are read as magnitudes: the question they answer is "is that
 * plausible for a bunker", and a decimetre either way has no bearing on it.
 */
export const formatArea = (squareMetres: number, unit: DistanceUnit): string => {
  if (!Number.isFinite(squareMetres) || squareMetres < 0) return '';
  return unit === 'imperial'
    ? `${Math.round(squareMetres * YARDS_PER_METRE * YARDS_PER_METRE)} yd²`
    : `${Math.round(squareMetres)} m²`;
};

/**
 * Formats a travel distance, switching to the larger unit once the small one stops reading
 * well. "21521 m" is technically right and useless; "21.5 km" is what a person wants.
 */
export const formatTravelDistance = (metres: number, unit: DistanceUnit): string => {
  if (!Number.isFinite(metres)) return '';

  if (unit === 'imperial') {
    if (metres < METRES_PER_MILE) return `${Math.round(metresToYards(metres))} yd`;
    return `${oneDecimal(metres / METRES_PER_MILE)} mi`;
  }

  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${oneDecimal(metres / 1000)} km`;
};

/**
 * Formats a height — the climb or drop a shot plays over.
 *
 * Feet rather than yards for imperial: golf quotes hole lengths in yards but elevation in
 * feet, and "12 ft uphill" is what a caddie says. Unsigned, because the direction is carried
 * by the uphill/downhill word beside it and a sign would only state it twice.
 */
export const formatElevation = (metres: number, unit: DistanceUnit): string => {
  if (!Number.isFinite(metres)) return '';
  const magnitude = Math.abs(metres);
  return unit === 'imperial'
    ? `${Math.round(magnitude * FEET_PER_METRE)} ft`
    : `${Math.round(magnitude)} m`;
};

/**
 * One decimal place, with a trailing ".0" dropped.
 *
 * Rounding to whole units loses too much up close — 21.5km reading as "22 km" — while a
 * forced decimal makes round numbers look oddly precise, as "48.0 km" does.
 */
const oneDecimal = (value: number): string => {
  const rounded = value.toFixed(1);
  return rounded.endsWith('.0') ? rounded.slice(0, -2) : rounded;
};

/** The unit a length input is captured in, so the label can say so. */
export const distanceInputSuffix = (unit: DistanceUnit): string =>
  unit === 'imperial' ? 'yd' : 'm';
